/** Section 39 - the Grail.
 *
 * The whole of the Grail is three facts and one rule.
 *
 *  1. **Where it is buried is a fact about the map, not a piece of state.**
 *     It is derived from `rootSeed` and the map's own passable cells, so it is
 *     the same cell in a saved game, in a replay and in a fresh run of the same
 *     seed, and no field had to be added to `GameState` (and therefore no save
 *     migration written) to hold it. The obelisks already count themselves for
 *     the player - `objects.ts` emits `obelisk:seen/total` - and that count is
 *     what a puzzle map is *drawn* from; the kernel never gates digging on it,
 *     exactly as the classic does not.
 *  2. **Digging is a whole day.** A hero may dig only with his full day's
 *     movement still in hand, and the dig spends all of it. That is what stops
 *     a hero sweeping a dozen cells an afternoon, and it is the only reason the
 *     search is a decision rather than a formality.
 *  3. **The Grail is a key, not a stat line.** It carries no modifiers. What it
 *     buys is the right to raise one building, in one town, once.
 *
 * The rule: a Grail structure is the only building whose prerequisite is not a
 * building. `buildIllegality` asks this module whether somebody in the town is
 * holding it, and `build` takes it off them when the structure goes up - a
 * Grail spent is spent, and a captured town keeps the building rather than
 * handing the relic back.
 */

import type { ArtifactDefId, HeroId, PlayerId } from "../ids.js";
import { asArtifactDefId } from "../ids.js";
import type { GameState, HeroState, TownState } from "../state.js";
import { cellCoords, indexOf, isPassable, isWaterCell, occupantAt } from "../map/create.js";
import { entityKeys } from "../util/iter.js";
import type { Ctx } from "./ctx.js";
import { patchHero } from "./ctx.js";
import { payFlag, payNum, payloadOf, playerMarkKey } from "./payload.js";

/** The one artifact the dig yields. Content ships it; nothing else uses the id. */
export const GRAIL_ARTIFACT: ArtifactDefId = asArtifactDefId("grail");

/**
 * The mark a dig leaves behind, as a map-object def id.
 *
 * A dig used to cost a hero his whole day and leave nothing at all on the map,
 * so a player with three heroes and no notes had no way to tell a cell they had
 * already spent a day on from one they had not - and the search that costs the
 * most days is the one where that matters most.
 *
 * The hole is deliberately NOT laid into the map's occupant array. Two rules
 * read that array and both would be wrong: `diggableCell` would call the cell
 * undiggable, and - much worse - `grailCell` enumerates the diggable cells to
 * pick the Nth, so every hole anybody dug would MOVE the Grail. A hole is a
 * mark on the ground, not a thing in the way: nothing blocks on it, nothing
 * visits it, and it is drawn because the renderer draws what the observation
 * lists rather than what the occupant array claims.
 *
 * Named here rather than in `execute.ts` for the same reason `GRAIL_ARTIFACT`
 * is: content ships the def, and this is the one file that knows what a dig is.
 */
export const DIG_SITE_OBJECT = "obs_dig_hole";

/**
 * The town-screen slot every faction's Grail structure occupies. It lives here
 * because `town.ts` has to gate building on it and `create.ts` has to resolve
 * the `build_grail` victory condition against it, and `create.ts` imports
 * `town.ts`: a constant in either of those two would close a cycle. This module
 * imports nothing from the subsystem, so both can reach it.
 */
export const GRAIL_SLOT = "grail";

/**
 * A 64-bit-ish integer mix, used only to turn a seed into a cell index. It is
 * not a hash anybody depends on - `hash/xxhash64.ts` is that - it is here so
 * the choice is a pure arithmetic function of the seed and cannot drift with a
 * change to the hashing module.
 */
function mix(n: number): number {
  let x = n >>> 0;
  x = Math.imul(x ^ (x >>> 16), 0x45d9f3b) >>> 0;
  x = Math.imul(x ^ (x >>> 16), 0x45d9f3b) >>> 0;
  return (x ^ (x >>> 16)) >>> 0;
}

/**
 * Could the Grail be buried here? Open ground on the surface with nothing
 * standing on it: a hero cannot dig inside a town, under a mine, or at sea.
 */
export function diggableCell(s: GameState, i: number): boolean {
  return diggableIn(s, i, townCells(s));
}

/** The cells a town hall stands on. One pass, so a scan of the map is linear. */
function townCells(s: GameState): ReadonlySet<number> {
  const out = new Set<number>();
  for (const id of Object.keys(s.towns).sort()) {
    const t = s.towns[id as keyof typeof s.towns] as TownState | undefined;
    if (t === undefined) continue;
    out.add(indexOf(s.map, t.x, t.y, t.z));
  }
  return out;
}

/**
 * `diggableCell` with the town set hoisted out.
 *
 * The public form rebuilt that set per cell, which made `grailCell` quadratic
 * in the towns on the board. That cost nothing while the only caller was a DIG
 * command - "asked once per DIG, a handful of times in a whole game" - but the
 * observation now asks for the buried cell as well, so the scan happens on
 * every look at a solved puzzle rather than a handful of times.
 */
function diggableIn(s: GameState, i: number, towns: ReadonlySet<number>): boolean {
  if (i < 0 || i >= s.map.moveCost.length) return false;
  const [, , z] = cellCoords(s.map, i);
  if (z !== 0) return false;
  if (!isPassable(s.map, i) || isWaterCell(s.map, i)) return false;
  if (occupantAt(s.map, i) !== null) return false;
  return !towns.has(i);
}

/**
 * The cell the Grail is buried in: the Nth diggable cell of the surface, where
 * N is the seed's own mix. Returns -1 for a map with nowhere to bury it.
 *
 * Deliberately a scan rather than a cached index. It is asked once per DIG
 * command - a handful of times in a whole game - and a cache would be one more
 * thing that can disagree with a loaded save.
 */
export function grailCell(s: GameState): number {
  const towns = townCells(s);
  let count = 0;
  const cells = s.map.width * s.map.height;
  for (let i = 0; i < cells; i++) if (diggableIn(s, i, towns)) count++;
  if (count === 0) return -1;
  const want = mix(s.rootSeed) % count;
  let seen = 0;
  for (let i = 0; i < cells; i++) {
    if (!diggableIn(s, i, towns)) continue;
    if (seen === want) return i;
    seen++;
  }
  return -1;
}

// ------------------------------------------------------------- the puzzle map
//
// The obelisks counted themselves and said so in a message - "obelisk:3/8" -
// and that was the whole of it: a number with nothing behind it. In the classic
// obelisks are the *only* way to find the Grail, because each one uncovers part
// of a picture of the map, and what is left covered at the end is where to dig.
//
// This is that, with the one substitution the shell can actually draw: the
// picture is the minimap, and the jigsaw is a coarse grid over it. The rules
// below are the classic's and the reasons they are these rules are:
//
//  - **The grail's own piece comes off LAST.** Not by luck - it is held out of
//    the order. Otherwise a lucky third obelisk hands a player the cell and the
//    other five are worth nothing, which is the opposite of a search.
//  - **The order is a function of `rootSeed` and nothing else.** So it is the
//    same picture in a save, a replay and a fresh run of the seed, and no field
//    had to be added to `GameState` to hold it - the same argument `grailCell`
//    is built on.
//  - **It is monotone.** `revealed` for `seen + 1` is a superset of `revealed`
//    for `seen`, because the count is a prefix of one fixed order. A puzzle map
//    that could take a piece back would be a puzzle map nobody trusts.

/**
 * The jigsaw. Forty-eight pieces over the whole map, which is the number that
 * makes each obelisk on a typical map worth several of them and still leaves
 * the last one something to do.
 */
export const PUZZLE_COLS = 8;
export const PUZZLE_ROWS = 6;
export const PUZZLE_PIECES = PUZZLE_COLS * PUZZLE_ROWS;

/** Which piece a cell falls in. -1 for a cell off the map or underground. */
export function puzzlePieceOf(s: GameState, cell: number): number {
  if (cell < 0 || cell >= s.map.moveCost.length) return -1;
  const [x, y, z] = cellCoords(s.map, cell);
  if (z !== 0) return -1;
  const cx = Math.min(PUZZLE_COLS - 1, Math.floor((x * PUZZLE_COLS) / s.map.width));
  const cy = Math.min(PUZZLE_ROWS - 1, Math.floor((y * PUZZLE_ROWS) / s.map.height));
  return cy * PUZZLE_COLS + cx;
}

/**
 * The order the pieces come off in: a seeded shuffle with the Grail's own piece
 * moved to the end.
 *
 * A Fisher-Yates over `mix` rather than an RNG stream, for the same reason
 * `grailCell` uses `mix`: this is a fact about the map, asked outside any
 * command, and drawing from a stream in state would make *looking at the puzzle
 * map* advance the game.
 */
export function puzzleOrder(s: GameState): readonly number[] {
  const out: number[] = [];
  for (let i = 0; i < PUZZLE_PIECES; i++) out.push(i);
  let r = mix(s.rootSeed ^ 0x9e3779b9);
  for (let i = PUZZLE_PIECES - 1; i > 0; i--) {
    r = mix(r);
    const j = r % (i + 1);
    const a = out[i]!;
    out[i] = out[j]!;
    out[j] = a;
  }
  const last = puzzlePieceOf(s, grailCell(s));
  if (last < 0) return out;
  const at = out.indexOf(last);
  if (at >= 0) { out.splice(at, 1); out.push(last); }
  return out;
}

/** How many obelisks this player has stood on, and how many the map holds. */
export function obeliskProgress(c: Ctx, owner: PlayerId): { seen: number; total: number } {
  let seen = 0, total = 0;
  for (const id of entityKeys(c.s.objects)) {
    const o = c.s.objects[id]!;
    if (o.removed) continue;
    const p = payloadOf(c, o);
    if (!(payFlag(p, "obelisk") || payNum(p, "obelisk", 0) > 0)) continue;
    total++;
    if (p[playerMarkKey(owner)] === true) seen++;
  }
  return { seen, total };
}

/** `terrain[i]` for a cell the puzzle does not show. Outside every terrain index. */
export const PUZZLE_HIDDEN = 0xff;

export interface PuzzleView {
  readonly seen: number;
  readonly total: number;
  readonly cols: number;
  readonly rows: number;
  /** One byte a piece, 1 where the picture shows through. */
  readonly revealed: Uint8Array;
  readonly width: number;
  readonly height: number;
  /**
   * The picture itself: one terrain index per surface cell inside a revealed
   * piece, and `PUZZLE_HIDDEN` everywhere else.
   *
   * It is here, and not left to the shell, for two reasons and the second is
   * the one that matters.
   *
   * The shell CANNOT draw it. A `PlayerObservation` masks the terrain of every
   * unseen cell to zero - correctly - so a puzzle map painted from the
   * observation is a picture of what the player has already scouted, which on
   * day two is nothing at all. That is the bug this field fixes: an obelisk was
   * visited, three pieces came off, and the ground under them was fog, so the
   * screen was still black and the feature looked broken.
   *
   * And the shell MUST NOT decide it. What an obelisk buys is a rule - it is
   * the whole point of the obelisk, and it is deliberately a look at land the
   * player has not walked - so the exemption from fog belongs in the kernel
   * where it can be read, not in a screen that quietly ignores `visibility`.
   * Handing over only the revealed cells means no screen can leak more than the
   * puzzle has uncovered even by accident.
   *
   * Terrain only: no roads, no rivers, no objects, no towns, no heroes. The
   * obelisks show the shape of the world, not what is standing on it.
   */
  readonly terrain: Uint8Array;
  /**
   * Where to dig, once every obelisk has been stood on. -1 until then: the
   * Grail's piece is the last one held back, so this is not a second gate but
   * the same one said out loud.
   */
  readonly cell: number;
  /** `cell` as map coordinates, or nulls. Saves every caller the arithmetic. */
  readonly x: number;
  readonly y: number;
}

/**
 * What this player may see of the puzzle right now.
 *
 * `floor(pieces * seen / total)` is the count, so the last piece needs the last
 * obelisk and every one before it is worth a real share of the picture.
 */
export function puzzleView(c: Ctx, owner: PlayerId): PuzzleView {
  const { seen, total } = obeliskProgress(c, owner);
  const revealed = new Uint8Array(PUZZLE_PIECES);
  const order = puzzleOrder(c.s);
  const take = total <= 0 ? 0 : Math.floor((PUZZLE_PIECES * seen) / total);
  for (let i = 0; i < take && i < order.length; i++) revealed[order[i]!] = 1;
  const solved = total > 0 && seen >= total;
  const cell = solved ? grailCell(c.s) : -1;
  const [x, y] = cell >= 0 ? cellCoords(c.s.map, cell) : [-1, -1];

  const { width, height } = c.s.map;
  const terrain = new Uint8Array(width * height).fill(PUZZLE_HIDDEN);
  for (let i = 0; i < width * height; i++) {
    // `puzzlePieceOf` takes a cell index into the whole map, and the surface is
    // the first plane, so `i` is already the surface cell.
    const piece = puzzlePieceOf(c.s, i);
    if (piece < 0 || revealed[piece] !== 1) continue;
    terrain[i] = c.s.map.terrain[i] ?? PUZZLE_HIDDEN;
  }

  return {
    seen, total, cols: PUZZLE_COLS, rows: PUZZLE_ROWS, revealed,
    width, height, terrain,
    cell, x: x ?? -1, y: y ?? -1,
  };
}

/** Is this hero carrying the Grail, worn or in the pack? */
export function carriesGrail(h: HeroState): boolean {
  if (h.backpack.includes(GRAIL_ARTIFACT)) return true;
  for (const slot of Object.keys(h.artifacts).sort()) {
    if (h.artifacts[slot] === GRAIL_ARTIFACT) return true;
  }
  return false;
}

/** The hero standing in this town with the Grail on him, if there is one. */
export function grailBearerIn(c: Ctx, t: TownState): HeroId | null {
  for (const id of [t.visitingHero, t.garrisonHero]) {
    if (id === null) continue;
    const h = c.s.heroes[id];
    if (h !== undefined && carriesGrail(h)) return id;
  }
  return null;
}

/** Take the Grail off a hero. Raising the structure spends it. */
export function spendGrail(c: Ctx, id: HeroId): void {
  const h = c.s.heroes[id];
  if (h === undefined) return;
  const artifacts: Record<string, ArtifactDefId | null> = {};
  for (const slot of Object.keys(h.artifacts).sort()) {
    const cur = h.artifacts[slot] ?? null;
    artifacts[slot] = cur === GRAIL_ARTIFACT ? null : cur;
  }
  const backpack = h.backpack.filter((a) => a !== GRAIL_ARTIFACT);
  patchHero(c, id, { artifacts, backpack });
}
